import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';

interface RecruiterFormProps {
  companyId: number;
  recruiter: {
    id: number;
    owner: boolean;
    active: boolean;
    pending: boolean;
    recruiter_id: number;
    email: string;
    name: string | null;
    publish_opportunity: boolean;
    candidates_database_access: boolean;
  };
  onSave: () => void;
  onCancel: () => void;
}

const RecruiterForm: React.FC<RecruiterFormProps> = ({ companyId, recruiter, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    owner: false,
    active: false,
    pending: false,
    publish_opportunity: false,
    candidates_database_access: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFormData({
      owner: recruiter.owner || false,
      active: recruiter.active || false,
      pending: recruiter.pending || false,
      publish_opportunity: recruiter.publish_opportunity || false,
      candidates_database_access: recruiter.candidates_database_access || false,
    });
  }, [recruiter]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put(`/companies/${companyId}/recruiters/${recruiter.id}`, formData);
      toast.success('Recrutador atualizado com sucesso!');
      onSave();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar recrutador');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Editar Recrutador</h2>
          <button onClick={onCancel} style={styles.closeBtn} type="button">×</button>
        </div>
        
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.infoSection}>
            <div style={styles.infoRow}>
              <strong>Nome:</strong> {recruiter.name || '-'}
            </div>
            <div style={styles.infoRow}>
              <strong>Email:</strong> {recruiter.email}
            </div>
          </div>

          <h3 style={styles.sectionTitle}>Recrutador</h3>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.owner}
                onChange={(e) => handleChange('owner', e.target.checked)}
              />
              Owner
            </label>
          </div>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => handleChange('active', e.target.checked)}
              />
              Ativo
            </label>
          </div>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.pending}
                onChange={(e) => handleChange('pending', e.target.checked)}
              />
              Pendente
            </label>
          </div>

          <h3 style={styles.sectionTitle}>Permissões</h3>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.publish_opportunity}
                onChange={(e) => handleChange('publish_opportunity', e.target.checked)}
              />
              Publicar Oportunidade
            </label>
          </div>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.candidates_database_access}
                onChange={(e) => handleChange('candidates_database_access', e.target.checked)}
              />
              Base de Candidatos
            </label>
          </div>
          
          <div style={styles.buttons}>
            <button
              type="button"
              onClick={onCancel}
              style={styles.cancelBtn}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              style={styles.saveBtn}
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem 2rem 0 2rem',
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
    padding: 0,
    lineHeight: 1,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '0 2rem 2rem 2rem',
  },
  infoSection: {
    backgroundColor: '#f5f5f5',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1.5rem',
    marginTop: '1rem',
  },
  infoRow: {
    marginBottom: '0.5rem',
    color: colors.text,
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: colors.primary,
    marginTop: '1.5rem',
    marginBottom: '1rem',
    borderBottom: `2px solid ${colors.primary}`,
    paddingBottom: '0.5rem',
  },
  checkboxGroup: {
    marginBottom: '1rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: colors.text,
    cursor: 'pointer',
    fontSize: '1rem',
  },
  buttons: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  cancelBtn: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: colors.border,
    color: colors.text,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  saveBtn: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
};

export default RecruiterForm;
